import { useState, type FormEvent } from 'react';
import { FileSignature, Download, Check, AlertCircle } from 'lucide-react';
import { api, ApiError } from '@/lib/api';
import { useApi } from '@/lib/useApi';
import { useAuth } from '@/lib/auth';
import { dateTime } from '@/lib/format';
import { saveFile } from '@/lib/download';
import {
  Badge, Button, Card, CardHeader, EmptyState, Field, Input, LoadingBlock, useToast,
} from '@/components/ui';
import type { PortalAgreement as PortalAgreementData } from '@shared/types';

/** Today, as the backend will compute it. Used to fill the date in for them. */
function todayIso(): string {
  const now = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

/**
 * Reading and signing the tenancy agreement.
 *
 * The whole screen is one document and one small form, because that is the
 * shape of the thing being done. No wizard, no stepper, no scroll-to-the-
 * bottom gate: a person who wants to read it will, and a gate that pretends to
 * prove they did is theatre.
 */
export default function PortalAgreement() {
  const { tenant } = useAuth();
  const toast = useToast();
  const { data, loading, reload } = useApi<PortalAgreementData>('/portal/agreement');

  const [name, setName] = useState('');
  const [signedDate, setSignedDate] = useState(todayIso());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function sign(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.post('/portal/agreement/sign', { full_name: name, signed_date: signedDate });
      toast.success('Signed. A copy is yours to download whenever you like.');
      reload();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'That could not be signed');
    } finally {
      setBusy(false);
    }
  }

  async function download(signatureId?: string) {
    try {
      saveFile(await api.post('/portal/agreement/pdf', { signature_id: signatureId }));
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not build that PDF');
    }
  }

  if (loading && !data) return <LoadingBlock rows={5} />;
  if (!data) return null;

  if (!data.agreement) {
    return (
      <EmptyState
        icon={<FileSignature className="h-6 w-6" />}
        title="Nothing to sign"
        message="Your landlord has not published a tenancy agreement here. Anything you have signed on paper still stands."
      />
    );
  }

  const signed = data.signature?.status === 'signed';

  return (
    <div className="space-y-5">
      <Card>
        <CardHeader
          title={data.agreement.name}
          subtitle={`Version ${data.agreement.version}`}
          action={signed
            ? <Badge tone="ok"><Check className="h-3 w-3" /> Signed</Badge>
            : <Badge tone="warn">Waiting for your signature</Badge>}
        />
        <div className="p-5 sm:p-6 pt-4 space-y-4">
          {data.needs_signature && data.agreement.change_note && (
            <div className="text-sm bg-warn/10 border border-warn/25 rounded-xl px-4 py-3">
              <p className="text-ink">What has changed since you last signed</p>
              <p className="text-muted mt-1">{data.agreement.change_note}</p>
            </div>
          )}

          <pre className="text-sm whitespace-pre-wrap leading-relaxed font-sans">
            {data.agreement.body}
          </pre>

          {signed && (
            <div className="flex items-center gap-3 flex-wrap pt-2 border-t border-line">
              <p className="text-sm text-muted flex-1">
                Signed by {data.signature?.signed_name} on{' '}
                {data.signature?.signed_at ? dateTime(data.signature.signed_at) : ''}.
              </p>
              <Button
                icon={<Download className="h-4 w-4" />}
                onClick={() => void download(data.signature?.id)}
              >
                Download a copy
              </Button>
            </div>
          )}
        </div>
      </Card>

      {data.needs_signature && (
        <Card>
          <CardHeader
            title="Sign"
            subtitle="Type your name and today’s date, exactly as your landlord has them"
          />
          <form onSubmit={sign} className="p-5 sm:p-6 pt-4 space-y-4 max-w-md">
            <Field label="Your full name" hint={tenant ? `On your tenancy: ${tenant.full_name}` : undefined}>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={tenant?.full_name ?? 'Your name'}
                autoComplete="name"
                required
              />
            </Field>

            <Field label="Date">
              <Input
                type="date"
                value={signedDate}
                onChange={(e) => setSignedDate(e.target.value)}
                required
              />
            </Field>

            {error && (
              <div className="flex items-start gap-2 text-sm text-danger bg-danger/10
                              border border-danger/25 rounded-xl px-3 py-2.5">
                <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
                <span>{error}</span>
              </div>
            )}

            {/*
              What signing here means, in the place where it is being done.
              A tenant is entitled to know it is not a witnessed signature
              before they type their name, not afterwards.
            */}
            <p className="text-xs text-faint leading-relaxed">
              Signing records that you agreed to the words above, from this account, today.
              You signed in with a code sent to your own phone. Keep a copy — you can
              download one at any time, including after the agreement is updated.
            </p>

            <Button type="submit" variant="primary" size="lg" loading={busy} className="w-full">
              Sign this agreement
            </Button>
          </form>
        </Card>
      )}

      {data.history.length > 0 && (
        <Card>
          <CardHeader title="What you have signed before" subtitle="Kept, and still yours to download" />
          <div className="p-5 sm:p-6 pt-4 space-y-2">
            {data.history.map((h) => (
              <div key={h.id} className="flex items-center gap-3 text-sm">
                <span className="flex-1 text-muted">
                  {h.name} · version {h.version} · signed{' '}
                  {h.signed_at ? dateTime(h.signed_at) : ''}
                </span>
                <Button size="sm" variant="ghost" onClick={() => void download(h.id)}>
                  Download
                </Button>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}
